import { useCallback, useEffect, useState } from "react";

// The native `storage` event only fires in *other* tabs, so we broadcast our own to keep every hook
// instance on a key in sync, i.e. the debug modal toggling a flag that the header reads.
const CHANGE_EVENT = "local-storage-change";

/**
 * A hook that syncs state with localStorage returning a tuple of [value, setValue] the same way as useState
 */
export function useLocalStorage<T>(key: string, defaultValue: T): [T, (value: T) => void] {
  const [state, setState] = useState<T>(() => {
    try {
      const stored = localStorage.getItem(key);
      return stored !== null ? JSON.parse(stored) : defaultValue;
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error);
      return defaultValue;
    }
  });

  const setAndStore = useCallback(
    (value: T) => {
      setState(value);

      try {
        localStorage.setItem(key, JSON.stringify(value));
        window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: key }));
      } catch (error) {
        console.error(`Error setting localStorage key "${key}":`, error);
      }
    },
    [key],
  );

  useEffect(() => {
    function onChange(e: Event) {
      if ((e as CustomEvent<string>).detail !== key) return;
      try {
        // Only fires after a write, so the key is always set by now and needs no default
        const stored = localStorage.getItem(key);
        if (stored !== null) setState(JSON.parse(stored));
      } catch (error) {
        console.error(`Error reading localStorage key "${key}":`, error);
      }
    }
    window.addEventListener(CHANGE_EVENT, onChange);
    return () => window.removeEventListener(CHANGE_EVENT, onChange);
  }, [key]);

  return [state, setAndStore];
}
